<template>
    <div>
        <div v-if="statusLoaded && ! isVerified" class="form-help m-t-2">
            {{ trans('We recommend using an email address that matches your domain name to improve email deliverability.') }}
            <a href="#verify-email-identity" v-on:click.prevent="open" class="m-l-1">{{ trans('Verify your email identity') }}</a>
        </div>

        <div v-if="isVerified" v-bind:class="{'has-error': error}" class="form-group">
            <label>
                {{ trans('From address') }}
                <input class="input__field" type="text" v-bind:name="fieldName" v-model="fromAddress" v-bind:placeholder="'noreply@' + domain">
            </label>
        </div>
        <div v-if="error" class="form-help">{{ error }}</div>

        <modal v-bind:show.sync="showModal" v-bind:title="trans('Verify your email identity')" size="medium">
            <div v-if=" ! statusLoaded" class="loader">
                <div class="chartdonut chartdonut_animation--rotate-tiny chartdonut--tiny">
                    <svg class="i-stroke-accent" width="24" height="24"><g><circle class="icon" cx="50%" cy="50%" r="10" stroke-width="2" fill="none" stroke-dashoffset="5"></circle></g></svg>
                </div>
            </div>

            <template v-else>
                <div v-if="status === 'SUCCESS'">
                    <p class="m-b-3">{{ trans('Your domain has been verified. You can now set a from address below that matches your domain.') }}</p>
                </div>

                <div v-else-if="status === 'PENDING'">
                    <p class="m-b-3">{{ trans('Add the following CNAME records to your DNS provider to verify your domain. This can take up to 72 hours to fully propagate.') }}</p>

                    <div v-for="record in dnsRecords" v-bind:key="record.name" class="alert p-3 c-muted br br-c--fade alert c-warning-bg m-b-2">
                        <div class="m-b-2">
                            <div class="f-s-1 small-2 m-b-1">{{ trans('Type') }}</div>
                            <div class="f-w-bold f-s-1">{{ record.type }}</div>
                        </div>
                        <div class="m-b-2">
                            <div class="f-s-1 small-2 m-b-1">{{ trans('Name') }}</div>
                            <div class="f-w-bold f-s-1">{{ record.name }}</div>
                        </div>
                        <div>
                            <div class="f-s-1 small-2 m-b-1">{{ trans('Value') }}</div>
                            <div class="f-w-bold f-s-1">{{ record.value }}</div>
                        </div>
                    </div>

                    <button v-on:click="refreshStatus" v-bind:disabled="loadingStatus" type="button" class="btn btn-outline btn-outline--bright m-t-2">
                        <span v-if=" ! loadingStatus">{{ trans('Check status') }}</span>
                        <div v-else class="loader">
                            <div class="chartdonut chartdonut_animation--rotate-tiny chartdonut--tiny">
                                <svg class="i-stroke-accent" width="24" height="24"><g><circle class="icon" cx="50%" cy="50%" r="10" stroke-width="2" fill="none" stroke-dashoffset="5"></circle></g></svg>
                            </div>
                        </div>
                    </button>
                </div>

                <div v-else-if="status === 'FAILED' || status === 'TEMPORARY_FAILURE'">
                    <p class="m-b-3">{{ trans('We could not verify your domain.') }}</p>

                    <button v-on:click="createDomain" v-bind:disabled="creating" type="button" class="btn btn-outline btn-outline--bright">
                        <span v-if=" ! creating">{{ trans('Try again') }}</span>
                        <div v-else class="loader">
                            <div class="chartdonut chartdonut_animation--rotate-tiny chartdonut--tiny">
                                <svg class="i-stroke-accent" width="24" height="24"><g><circle class="icon" cx="50%" cy="50%" r="10" stroke-width="2" fill="none" stroke-dashoffset="5"></circle></g></svg>
                            </div>
                        </div>
                    </button>
                </div>

                <div v-else>
                    <p class="m-b-3">{{ trans('Verifying your domain lets you send emails from an address that matches your domain name, improving deliverability.') }}</p>

                    <button v-on:click="createDomain" v-bind:disabled="creating" type="button" class="btn btn-outline btn-outline--bright">
                        <span v-if=" ! creating">{{ trans('Create sending domain') }}</span>
                        <div v-else class="loader">
                            <div class="chartdonut chartdonut_animation--rotate-tiny chartdonut--tiny">
                                <svg class="i-stroke-accent" width="24" height="24"><g><circle class="icon" cx="50%" cy="50%" r="10" stroke-width="2" fill="none" stroke-dashoffset="5"></circle></g></svg>
                            </div>
                        </div>
                    </button>
                </div>

                <div v-if="requestError" class="form-help">{{ requestError }}</div>
            </template>

            <div slot="footer">
                <div class="row row-collapse">
                    <div class="column">
                        <a v-on:click.prevent="showModal = false" href="#close" class="btn btn-primary m-r-3">{{ trans('Close') }}</a>
                    </div>
                </div>
            </div>
        </modal>
    </div>
</template>
<script>
import Modal from '../modals/Modal.vue';

export default {
    components: {
        Modal,
    },
    props: {
        domain: {
            type: String,
            default: null,
        },
        fieldName: {
            type: String,
            required: true,
        },
        initialFromAddress: {
            type: String,
            default: null,
        },
        error: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            showModal: false,
            // A saved from address can only exist once verification has
            // succeeded, so we can safely seed the status with it to avoid
            // flickering the field in/out while the real status loads.
            status: this.initialFromAddress ? 'SUCCESS' : null,
            statusLoaded: !! this.initialFromAddress,
            loadingStatus: false,
            creating: false,
            dnsRecords: [],
            requestError: null,
            fromAddress: this.initialFromAddress,
        }
    },
    computed: {
        isVerified() {
            return this.status === 'SUCCESS';
        }
    },
    methods: {
        open() {
            this.showModal = true;
        },
        fetchStatus() {
            this.loadingStatus = true;
            this.requestError = null;

            window.axios.get(Kourses.apiBaseUrl + '/settings/email-identity')
                .then((response) => {
                    this.status = response.data.status;
                    this.statusLoaded = true;
                    this.loadingStatus = false;

                    if (this.status === 'PENDING') {
                        this.fetchDnsRecords();
                    }
                })
                .catch((error) => {
                    this.statusLoaded = true;
                    this.loadingStatus = false;
                    this.requestError = this.errorMessage(error);
                });
        },
        refreshStatus() {
            this.fetchStatus();
        },
        fetchDnsRecords() {
            window.axios.get(Kourses.apiBaseUrl + '/settings/email-identity/dns-records')
                .then((response) => {
                    this.dnsRecords = response.data.records;
                });
        },
        createDomain() {
            this.creating = true;
            this.requestError = null;

            window.axios.post(Kourses.apiBaseUrl + '/settings/email-identity')
                .then((response) => {
                    this.creating = false;
                    this.dnsRecords = response.data.records;
                    this.status = 'PENDING';
                })
                .catch((error) => {
                    this.creating = false;
                    this.requestError = this.errorMessage(error);
                });
        },
        errorMessage(error) {
            if (error.response && error.response.data && error.response.data.message) {
                return error.response.data.message;
            }

            return this.trans('Something went wrong. Please try again.');
        }
    },
    mounted() {
        if (this.domain) {
            this.fetchStatus();
        }

        // Auto-open the modal window if hash is what we want
        if (window.location.hash && window.location.hash.substring(1) === 'verify-email-identity') {
            this.showModal = true;
        }
    }
}
</script>
